/* eslint-disable */
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/index";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";
import { toast } from "sonner";
import {
  ArrowLeft, MessageSquare, PhoneCall, UserCog, Edit2,
  ClipboardList, Pill, TrendingUp, Building2, X,
  AlertTriangle, CheckCircle, ArrowUpRight, Hospital, Eye,
  Activity, FileText, Send, Calendar, UserX
} from "lucide-react";
import RdvRequestModal from "@/components/shared/RdvRequestModal";
import usePatientDetailData from "@/components/patient/detail/usePatientDetailData";
import usePatientDocuments from "@/components/patient/detail/usePatientDocuments";
import useProfessionalActionTrace from "@/components/patient/detail/useProfessionalActionTrace";
import { PATIENT_DETAIL_TABS } from "@/components/patient/detail/patientDetailTabs";
import PatientDetailTabPanels from "@/components/patient/detail/PatientDetailTabPanels";
import PatientClinicalUpdateModals from "@/components/patient/detail/PatientClinicalUpdateModals";
import EscalationFollowupModal from "@/components/clinical/EscalationFollowupModal";
import TransmissionStructuredFields from "@/components/patient/TransmissionStructuredFields";

const RESOLUTION_OUTCOME_STYLES = {
  red: { selected: "border-red-500 bg-red-50", iconBg: "bg-red-100" },
  green: { selected: "border-green-500 bg-green-50", iconBg: "bg-green-100" },
  sky: { selected: "border-sky-500 bg-sky-50", iconBg: "bg-sky-100" },
  slate: { selected: "border-slate-500 bg-slate-100", iconBg: "bg-slate-200" },
};

const getResolutionOutcomeStyles = (color) => RESOLUTION_OUTCOME_STYLES[color] || RESOLUTION_OUTCOME_STYLES.sky;

// Import shared patient components
import {
  PatientDetailHeader,
  PatientDetailTabs,
  StatusChangeModal,
  EditPatientModal,
  CareSummaryButton,
  DeactivatePatientModal,
  DossierStatusModal,
  PatientActionToolbar,
} from "@/components/patient";

export default function NursePatientDetail() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const { patient, checkins, alerts, transmissions, documents, studyEnrollments, loading, loadError, fetchAll } = usePatientDetailData(patientId);
  const [activeTab, setActiveTab] = useState("overview");

  // Status change modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showDossierModal, setShowDossierModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [transmission, setTransmission] = useState("");

  // Alert resolution modal
  const [showResolveModal, setShowResolveModal] = useState(null);
  const [escalationResult, setEscalationResult] = useState(null);
  const [resolveOutcome, setResolveOutcome] = useState("");
  const [resolveNotes, setResolveNotes] = useState("");
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveAction, setResolveAction] = useState("resolve");
  const [escalationTransmission, setEscalationTransmission] = useState("");

  // Document upload
  const { uploading, handleFileUpload, handleDownload, handleDeleteDoc } = usePatientDocuments(patientId, fetchAll);
  const [pwaReminderLoading, setPwaReminderLoading] = useState(false);

  // Edit patient modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    cancerType: "",
    cancerTypeDetail: "",
    treatmentType: "",
    therapeuticLine: "",
    protocol: "",
  });
  const [editLoading, setEditLoading] = useState(false);

  // Clinical update modals
  const [showTreatmentEvolution, setShowTreatmentEvolution] = useState(false);
  const [showDiseaseProgression, setShowDiseaseProgression] = useState(false);
  const [showClinicalEvent, setShowClinicalEvent] = useState(false);

  // Transmission modal
  const [showTransmissionModal, setShowTransmissionModal] = useState(false);
  const [transmissionContent, setTransmissionContent] = useState("");
  const [transmissionCategory, setTransmissionCategory] = useState("NOTE");
  const [transmissionPriority, setTransmissionPriority] = useState("INFO");
  const [transmissionTags, setTransmissionTags] = useState([]);
  const [transmissionActionRequired, setTransmissionActionRequired] = useState("NONE");
  const [transmissionTreatmentRelated, setTransmissionTreatmentRelated] = useState(false);
  const [transmissionOralAdherenceRelated, setTransmissionOralAdherenceRelated] = useState(false);
  const [transmissionAddToHandover, setTransmissionAddToHandover] = useState(true);
  const [transmissionLoading, setTransmissionLoading] = useState(false);

  const resetTransmissionForm = () => {
    setTransmissionContent("");
    setTransmissionCategory("NOTE");
    setTransmissionPriority("INFO");
    setTransmissionTags([]);
    setTransmissionActionRequired("NONE");
    setTransmissionTreatmentRelated(false);
    setTransmissionOralAdherenceRelated(false);
    setTransmissionAddToHandover(true);
  };

  // RDV Request modal
  const [showRdvRequestModal, setShowRdvRequestModal] = useState(false);

  const handleResolveAlert = async (alertId) => {
    setShowResolveModal(alertId);
    setResolveOutcome("");
    setResolveNotes("");
    setResolveAction("resolve");
    setEscalationTransmission("");
  };

  const submitResolveAlert = async () => {
    // Escalation flow
    if (resolveAction === "escalate") {
      if (escalationTransmission.length < 10) {
        toast.error("La transmission est obligatoire (minimum 10 caractères)");
        return;
      }
      setResolveLoading(true);
      try {
        const res = await api.patch(`/alerts/${showResolveModal}/escalate`, {
          transmission: escalationTransmission,
          notes: resolveNotes,
        });
        toast.success("Alerte escaladée au médecin");
        setEscalationResult(res.data);
        setShowResolveModal(null);
        fetchAll();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur");
      } finally {
        setResolveLoading(false);
      }
      return;
    }

    // Resolution flow
    if (!resolveOutcome) {
      toast.error("Veuillez sélectionner une issue");
      return;
    }
    setResolveLoading(true);
    try {
      await api.patch(`/alerts/${showResolveModal}/resolve`, {
        notes: resolveNotes,
        resolutionOutcome: resolveOutcome,
      });
      toast.success("Alerte résolue");
      if (resolveOutcome === "hospitalisation_necessaire") toast.info("Si l'hospitalisation est confirmée, déclarez-la via « Plus d'actions » : le dossier passe en Hospitalisé.");
      setShowResolveModal(null);
      fetchAll();
    } catch {
      toast.error("Erreur");
    } finally {
      setResolveLoading(false);
    }
  };

  const traceProfessionalAction = useProfessionalActionTrace(patientId, "nurse_patient_detail");

  const handleCallPatient = () => {
    traceProfessionalAction({
      actionType: "CALL_PATIENT",
      targetModule: "patient_contact",
      channel: "PHONE",
      outcome: "TEL_LINK_OPENED",
    });
    window.location.href = `tel:${patient.phone}`;
  };

  const handlePwaCheckinReminder = async () => {
    setPwaReminderLoading(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/checkin-reminder/pwa`);
      traceProfessionalAction({
        actionType: "PWA_CHECKIN_REMINDER_SENT",
        targetModule: "patient_contact",
        channel: "PWA",
        outcome: data?.pushSent ? "PUSH_AND_IN_APP" : "IN_APP_ONLY",
      });
      toast.success(data?.pushSent ? "Rappel PWA envoyé" : "Notification créée dans l’app patient");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur rappel PWA");
    } finally {
      setPwaReminderLoading(false);
    }
  };

  const openClinicalEvaluation = () => {
    traceProfessionalAction({ actionType: "CLINICAL_EVALUATION_OPENED", targetModule: "clinical", outcome: "NAVIGATION" });
    navigate(`/nurse/patients/${patientId}/evaluation`);
  };

  const handleEditPatient = async () => {
    setEditLoading(true);
    try {
      await api.patch(`/patients/${patientId}`, {
        cancerType: editForm.cancerType || null,
        cancerTypeDetail: editForm.cancerTypeDetail || null,
        treatmentType: editForm.treatmentType || null,
        therapeuticLine: editForm.therapeuticLine || null,
        protocol: editForm.protocol || null,
      });
      toast.success("Patient mis à jour");
      setShowEditModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </ProfessionalLayout>
    );
  }

  if (!patient) {
    return (
      <ProfessionalLayout>
        <div className="max-w-xl mx-auto mt-16 text-center space-y-4" data-testid="patient-detail-load-error">
          <p className="text-slate-700 font-medium">{loadError || "Patient indisponible"}</p>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700" data-testid="patient-detail-back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour à la liste
          </button>
        </div>
      </ProfessionalLayout>
    );
  }

  const activeAlerts = alerts.filter(a => a.status !== "RESOLVED");

  // Nurse-specific action toolbar : 1 action principale + secondaires + « Plus d'actions »
  const evaluationComplete = Boolean(patient.clinicalEvaluationComplete);
  const openWhatsAppReminder = () => {
    traceProfessionalAction({ actionType: "WHATSAPP_REMINDER_OPENED", targetModule: "patient_contact", channel: "WHATSAPP", outcome: "EXTERNAL_LINK_OPENED" });
    const phone = patient.phone?.replace("+", "");
    const loginUrl = `${window.location.origin}/login`;
    const msg = encodeURIComponent(`Bonjour ${patient.firstName}, votre équipe soignante vous invite à vous connecter à Hana pour compléter votre suivi : ${loginUrl}. Cela ne prend que 2 minutes et aide votre médecin à mieux vous accompagner. Merci !`);
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };
  const actionButtons = (
    <PatientActionToolbar
      primary={{
        label: evaluationComplete ? "Évaluation clinique" : "Compléter l'évaluation",
        icon: ClipboardList,
        onClick: openClinicalEvaluation,
        testId: evaluationComplete ? "edit-evaluation-btn" : "complete-evaluation-btn",
      }}
      secondary={[
        { label: "Appeler", icon: PhoneCall, onClick: handleCallPatient, testId: "call-patient-btn" },
        { label: "Message", icon: MessageSquare, testId: "message-patient-btn", onClick: () => { traceProfessionalAction({ actionType: "MESSAGE_THREAD_OPENED", targetModule: "messages", channel: "IN_APP", outcome: "NAVIGATION" }); navigate("/nurse/messages", { state: { patientId, patientName: `${patient.firstName} ${patient.lastName}` } }); } },
        { label: "Transmission", icon: FileText, testId: "create-transmission-btn", onClick: () => { traceProfessionalAction({ actionType: "TRANSMISSION_MODAL_OPENED", targetModule: "transmissions", outcome: "MODAL_OPENED" }); setShowTransmissionModal(true); } },
      ]}
      extra={<CareSummaryButton patientId={patientId} />}
      more={[
        { label: "Rappel WhatsApp", icon: Send, testId: "whatsapp-reminder-btn", onClick: openWhatsAppReminder },
        { label: "Rappel check-in (PWA)", icon: Activity, testId: "pwa-checkin-reminder-btn", disabled: pwaReminderLoading, onClick: handlePwaCheckinReminder },
        { label: "Demander un RDV", icon: Calendar, testId: "request-rdv-btn", onClick: () => { traceProfessionalAction({ actionType: "RDV_REQUEST_MODAL_OPENED", targetModule: "appointments", outcome: "MODAL_OPENED" }); setShowRdvRequestModal(true); } },
        evaluationComplete && { label: "Évolution du traitement", icon: Pill, testId: "treatment-evolution-btn", separatorBefore: true, onClick: () => { traceProfessionalAction({ actionType: "TREATMENT_EVOLUTION_MODAL_OPENED", targetModule: "clinical", outcome: "MODAL_OPENED" }); setShowTreatmentEvolution(true); } },
        evaluationComplete && { label: "Réponse au traitement", icon: TrendingUp, testId: "disease-progression-btn", onClick: () => { traceProfessionalAction({ actionType: "RESPONSE_EVAL_MODAL_OPENED", targetModule: "clinical", outcome: "MODAL_OPENED" }); setShowDiseaseProgression(true); } },
        evaluationComplete && { label: "Événement clinique", icon: Building2, testId: "clinical-event-btn", onClick: () => { traceProfessionalAction({ actionType: "CLINICAL_EVENT_MODAL_OPENED", targetModule: "clinical", outcome: "MODAL_OPENED" }); setShowClinicalEvent(true); } },
        { label: "Corriger le statut clinique", icon: Edit2, testId: "change-status-btn", separatorBefore: true, onClick: () => { traceProfessionalAction({ actionType: "STATUS_MODAL_OPENED", targetModule: "patients", previousStatus: patient.status, outcome: "MODAL_OPENED" }); setNewStatus(patient.status); setShowStatusModal(true); } },
        { label: patient.dossierStatus === "hospitalise" ? "Fin d'hospitalisation" : "Déclarer une hospitalisation confirmée", icon: Hospital, testId: "dossier-status-btn", onClick: () => setShowDossierModal(true) },
        patient.isActive !== false && { label: "Désactiver le suivi", icon: UserX, testId: "deactivate-patient-btn", danger: true, onClick: () => setShowDeactivate(true) },
      ]}
    />
  );

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à la liste
        </button>

        {/* Patient Header */}
        <PatientDetailHeader
          patient={patient}
          patientId={patientId}
          onCallPatient={handleCallPatient}
          actionButtons={actionButtons}
          showEvaluationBanner={true}
          userRole="nurse"
          studyEnrollments={studyEnrollments}
        />

        {/* Check-in Frequency Selector */}
        <CheckinFrequencySelector patient={patient} patientId={patientId} onUpdate={fetchAll} />

        {/* Tabs */}
        <PatientDetailTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          checkinsCount={checkins.length}
          alertsCount={alerts.length}
          tabs={PATIENT_DETAIL_TABS}
        />

        {showDossierModal && (
          <DossierStatusModal patient={patient} onClose={() => setShowDossierModal(false)} onDone={() => { setShowDossierModal(false); fetchAll(); }} />
        )}

        {showDeactivate && (
          <DeactivatePatientModal patient={patient} onClose={() => setShowDeactivate(false)} onDone={() => { setShowDeactivate(false); fetchAll(); }} />
        )}

        <PatientDetailTabPanels
          activeTab={activeTab}
          patientId={patientId}
          patient={patient}
          checkins={checkins}
          alerts={alerts}
          transmissions={transmissions}
          documents={documents}
          uploading={uploading}
          onResolveAlert={handleResolveAlert}
          onFileUpload={handleFileUpload}
          onDownload={handleDownload}
          onDeleteDoc={handleDeleteDoc}
          onRefresh={fetchAll}
          userRole="nurse"
          editPath={`/nurse/patients/${patientId}/evaluation`}
        />
      </div>

      {/* Status Change Modal */}
      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        patientId={patientId}
        currentStatus={patient.status}
        statusMeta={patient.statusMeta}
        newStatus={newStatus}
        setNewStatus={setNewStatus}
        transmission={transmission}
        setTransmission={setTransmission}
        onUpdated={fetchAll}
      />

      {/* Alert Resolution Modal - Nurse version with escalation */}
      <EscalationFollowupModal result={escalationResult} patientId={patientId} onClose={() => setEscalationResult(null)} />
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b bg-emerald-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Traiter l&rsquo;alerte
                </h2>
                <button onClick={() => setShowResolveModal(null)} className="p-2 hover:bg-white/50 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Action Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Action *</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setResolveAction("resolve")}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                      resolveAction === "resolve"
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Résoudre
                  </button>
                  <button
                    onClick={() => setResolveAction("escalate")}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                      resolveAction === "escalate"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <ArrowUpRight className="w-4 h-4" />
                    Escalader au médecin
                  </button>
                </div>
              </div>

              {/* Resolution Flow */}
              {resolveAction === "resolve" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Issue de cette alerte *</label>
                  <div className="space-y-2">
                    {[
                      { value: "hospitalisation_necessaire", label: "Hospitalisation nécessaire", desc: "Le patient a dû être hospitalisé", color: "red" },
                      { value: "hospitalisation_evitee", label: "Hospitalisation évitée", desc: "Situation gérée en ambulatoire", color: "green" },
                      { value: "simple_surveillance", label: "Simple surveillance", desc: "Alerte mineure, surveillance à domicile", color: "sky" },
                      { value: "erreur_declaration", label: "Erreur de déclaration", desc: "Fausse alerte vérifiée avec le patient — justification obligatoire", color: "slate" },
                    ].map(opt => {
                      const styles = getResolutionOutcomeStyles(opt.color);
                      return (
                      <button
                        key={opt.value}
                        onClick={() => setResolveOutcome(opt.value)}
                        className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                          resolveOutcome === opt.value
                            ? styles.selected
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${resolveOutcome === opt.value ? styles.iconBg : "bg-slate-100"}`}>
                          {opt.color === "red" ? <Hospital className="w-5 h-5 text-red-600" /> :
                           opt.color === "green" ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                           <Eye className="w-5 h-5 text-sky-600" />}
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-slate-800">{opt.label}</p>
                          <p className="text-xs text-slate-500">{opt.desc}</p>
                        </div>
                      </button>
                    );})}
                  </div>
                </div>
              )}

              {/* Escalation Flow */}
              {resolveAction === "escalate" && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <ArrowUpRight className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800 text-sm">Escalade au médecin</p>
                      <p className="text-xs text-blue-600 mt-1">
                        L&rsquo;alerte sera transmise au médecin référent pour validation.
                        Une transmission détaillée est obligatoire.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Transmission for escalation */}
              {resolveAction === "escalate" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Transmission obligatoire *
                  </label>
                  <textarea
                    value={escalationTransmission}
                    onChange={e => setEscalationTransmission(e.target.value)}
                    placeholder="Décrivez la situation clinique, vos observations et la raison de l'escalade... (minimum 10 caractères)"
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 outline-none"
                    data-testid="escalation-transmission"
                  />
                  <p className="text-xs text-slate-400 mt-1">{escalationTransmission.length}/10 caractères minimum</p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optionnel)</label>
                <textarea
                  value={resolveNotes}
                  onChange={e => setResolveNotes(e.target.value)}
                  placeholder="Commentaires additionnels..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowResolveModal(null)}
                  className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  onClick={submitResolveAlert}
                  disabled={resolveLoading || (resolveAction === "resolve" && (!resolveOutcome || (resolveOutcome === "erreur_declaration" && resolveNotes.trim().length < 10))) || (resolveAction === "escalate" && escalationTransmission.length < 10)}
                  className={`flex-1 py-2.5 rounded-xl font-medium disabled:opacity-50 ${
                    resolveAction === "escalate"
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  }`}
                  data-testid="submit-alert-action"
                >
                  {resolveLoading ? "..." : resolveAction === "escalate" ? "Escalader" : "Résoudre"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Patient Modal */}
      <EditPatientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        patient={patient}
        editForm={editForm}
        setEditForm={setEditForm}
        onConfirm={handleEditPatient}
        loading={editLoading}
      />

      {/* Clinical Update Modals */}
      <PatientClinicalUpdateModals
        patientId={patientId}
        patient={patient}
        showTreatmentEvolution={showTreatmentEvolution}
        showDiseaseProgression={showDiseaseProgression}
        showClinicalEvent={showClinicalEvent}
        onCloseTreatmentEvolution={() => setShowTreatmentEvolution(false)}
        onCloseDiseaseProgression={() => setShowDiseaseProgression(false)}
        onCloseClinicalEvent={() => setShowClinicalEvent(false)}
        onRefresh={fetchAll}
      />

      {/* Transmission Modal */}
      {showTransmissionModal && (        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="transmission-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Nouvelle transmission</h3>
              <button onClick={() => setShowTransmissionModal(false)} data-testid="transmission-modal-close-button" className="p-1 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Catégorie</label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: "NOTE", label: "Note", color: "bg-slate-100 text-slate-700 border-slate-300" },
                  { value: "OBSERVATION", label: "Observation", color: "bg-blue-50 text-blue-700 border-blue-300" },
                  { value: "ALERTE", label: "Alerte", color: "bg-orange-50 text-orange-700 border-orange-300" },
                  { value: "CONSIGNE", label: "Consigne", color: "bg-purple-50 text-purple-700 border-purple-300" },
                ].map(c => (
                  <button
                    key={c.value}
                    onClick={() => setTransmissionCategory(c.value)}
                    data-testid={`transmission-cat-${c.value}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      transmissionCategory === c.value
                        ? `${c.color} border-2 shadow-sm`
                        : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Contenu</label>
              <textarea
                value={transmissionContent}
                onChange={e => setTransmissionContent(e.target.value)}
                placeholder="Écrire la transmission..."
                rows={4}
                data-testid="transmission-content"
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              />
            </div>
            <TransmissionStructuredFields
              priority={transmissionPriority}
              setPriority={setTransmissionPriority}
              clinicalTags={transmissionTags}
              setClinicalTags={setTransmissionTags}
              actionRequired={transmissionActionRequired}
              setActionRequired={setTransmissionActionRequired}
              treatmentRelated={transmissionTreatmentRelated}
              setTreatmentRelated={setTransmissionTreatmentRelated}
              oralAdherenceRelated={transmissionOralAdherenceRelated}
              setOralAdherenceRelated={setTransmissionOralAdherenceRelated}
              prefix="nurse-transmission"
            />
            <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-slate-700" data-testid="transmission-handover-toggle-row">
              <input
                type="checkbox"
                checked={transmissionAddToHandover}
                onChange={e => setTransmissionAddToHandover(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-700"
                data-testid="transmission-add-to-handover-checkbox"
              />
              <span><strong>Visible dans la relève IDE</strong><br /><span className="text-xs text-slate-500">À garder coché si une collègue doit comprendre ou reprendre ce point.</span></span>
            </label>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowTransmissionModal(false)}
                data-testid="transmission-cancel-button"
                className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  if (!transmissionContent.trim() || transmissionContent.trim().length < 5) {
                    toast.error("La transmission doit contenir au moins 5 caractères");
                    return;
                  }
                  setTransmissionLoading(true);
                  try {
                    await api.post(`/patients/${patientId}/transmissions`, {
                      content: transmissionContent.trim(),
                      category: transmissionCategory,
                      priority: transmissionPriority,
                      clinicalTags: transmissionTags,
                      actionRequired: transmissionActionRequired,
                      treatmentRelated: transmissionTreatmentRelated,
                      oralAdherenceRelated: transmissionOralAdherenceRelated,
                      addToHandover: transmissionAddToHandover,
                    });
                    toast.success("Transmission créée");
                    setShowTransmissionModal(false);
                    resetTransmissionForm();
                    fetchAll();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Erreur");
                  } finally {
                    setTransmissionLoading(false);
                  }
                }}
                disabled={transmissionLoading || transmissionContent.trim().length < 5}
                data-testid="submit-transmission-btn"
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {transmissionLoading ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RDV Request Modal — composant partagé IDE/MEDECIN */}
      {showRdvRequestModal && (
        <RdvRequestModal
          patientId={patientId}
          onClose={() => setShowRdvRequestModal(false)}
        />
      )}
    </ProfessionalLayout>
  );
}



function CheckinFrequencySelector({ patient, patientId, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const current = patient.checkinFrequency || "DAILY";

  const FREQUENCIES = [
    { value: "DAILY", label: "Tous les jours", desc: "Check-in quotidien" },
    { value: "EVERY_2_DAYS", label: "1 jour sur 2", desc: "Check-in un jour sur deux" },
    { value: "3X_WEEK", label: "3x / semaine", desc: "Lun, Mer, Ven" },
    { value: "1X_WEEK", label: "1x / semaine", desc: "Check-in hebdomadaire" },
  ];

  const handleChange = async (freq) => {
    if (freq === current) return;
    setSaving(true);
    try {
      await api.patch(`/patients/${patientId}/checkin-frequency`, { frequency: freq });
      toast.success(`Fréquence mise à jour : ${FREQUENCIES.find(f => f.value === freq)?.label}`);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dash-card" data-testid="checkin-frequency-selector">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-slate-700">Fréquence des check-ins</p>
          <p className="text-xs text-slate-400">Définit le rythme des check-ins attendus et des rappels envoyés au patient</p>
        </div>
        {saving && <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {FREQUENCIES.map(f => (
          <button
            key={f.value}
            onClick={() => handleChange(f.value)}
            disabled={saving}
            data-testid={`freq-btn-${f.value}`}
            className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
              current === f.value
                ? "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm"
                : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            <p className="font-semibold">{f.label}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{f.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
